/**
 * 18 Oct 2026 — the Companion & Wellness items (the Companion tab, Today's Needs your attention, the program's own
 * page) in the reader's language, and the one place each item opens. The API sends each item's kind and values
 * (`data`, `lines`); the sentence is written here through i18next. The API's English sentence (`text`, `sub`) stays
 * the fallback for an item this page has no words for (an older answer, a kind added later).
 */
import type { TFunction } from 'i18next';
import type { CompanionItem, CompanionItemLine } from '@shared/volunteers-api';
import { activityWords, minutesLabel, type CategoryKey, type TripKind } from '@shared/volunteers';
import { formatDayKey } from '@/lib/dates';
import { kindWords } from './kindWords';
import { visionsItemWords } from './Visions';

type Words = { text: string; sub: string };
const L = (lang: string): 'en' | 'es' => (lang === 'es' ? 'es' : 'en');
const s = (v: unknown): string => (v === null || v === undefined ? '' : String(v));
const has = (d: CompanionItem['data'], ...keys: string[]) => keys.every((k) => d[k] !== undefined && d[k] !== null && d[k] !== '');

/**
 * 19 Oct 2026 (D-1019-ATTN): the items that are Companion & Wellness's own. A new volunteer to review is a Volunteers
 * matter (they serve every program): it is listed under Volunteers on Today and at the top of the roster, not here.
 */
export const companionOnly = <T extends Pick<CompanionItem, 'kind'>>(items: readonly T[]): T[] => items.filter((i) => i.kind !== 'newVolunteer');

/**
 * The items one program's page shows (25 Oct 2026, D-1025-PROGRAM; the rules ProgramHome has had since 20/23 Oct): an event
 * item (an offer of help, a workshop) is Events', never a program page's; Reading's close-out and notes are Reading's
 * alone; an item about a request goes to that request's own program; an item about no request is Companion's.
 */
export function itemsForProgram(items: readonly CompanionItem[], requests: readonly { requestId: string; program: string }[], program: 'companion' | 'reading'): CompanionItem[] {
  const byRequest = new Map(requests.map((r) => [r.requestId, r.program === 'READING' ? 'reading' : 'companion'] as const));
  return companionOnly(items).filter((i) => {
    if (i.kind === 'activityHelp' || i.kind === 'workshopProposal' || i.kind === 'activityCloseout') return false;
    if (i.data.program === 'EVENTS') return false;
    if (i.data.program === 'READING') return program === 'reading';
    if (i.kind === 'readingCloseout' || i.kind === 'readingNote') return program === 'reading';
    const id = typeof i.data.requestId === 'string' && i.data.requestId ? i.data.requestId : null;
    return ((id && byRequest.get(id)) || 'companion') === program;
  });
}

/** A visit's kind in the reader's words (a group outing included). */
function kindOf(v: { category?: unknown; trip?: unknown; otherWhat?: unknown }, t: TFunction, lang: string): string {
  if (v.category === 'outing') return t('compItem.outing');
  return activityWords(s(v.category) as CategoryKey, (v.trip ?? null) as TripKind | null, L(lang), v.otherWhat ? s(v.otherWhat) : null);
}

const day = (key: unknown, lang: string) => (s(key) ? formatDayKey(s(key), lang) : '');
const time = (m: unknown) => (typeof m === 'number' ? minutesLabel(m) : '');

/** The Director's-OK item in the reader's language (the API's English stays the fallback). */
export function needsOkWords(i: CompanionItem, t: TFunction, lang: string): Words | null {
  if (i.kind !== 'needsOk') return null;
  const d = i.data;
  const who = d.guest ? t('vol.req.withGuest', { name: String(d.guest) }) : d.tenantFirst ? t('vol.req.withTenant', { name: String(d.tenantFirst), unit: String(d.tenantUnit ?? '') }) : '';
  return {
    text: t('vol.ok.itemText', { unit: String(d.unit ?? ''), first: String(d.first ?? ''), activity: kindWords(t, { category: String(d.activity), otherWhat: d.otherWhat ? String(d.otherWhat) : '' }), day: formatDayKey(String(d.dayKey ?? ''), lang) }),
    sub: who ? t('vol.ok.itemSub', { who }) : t('vol.ok.itemSubPlain'),
  };
}

const lineList = (lines: CompanionItemLine[] | undefined, one: (l: CompanionItemLine) => string) => (lines ?? []).map(one).join(' · ');

/** Every Companion item in the reader's language; `null` only when the item carries too little to write it. */
export function companionItemWords(i: CompanionItem, t: TFunction, lang: string): Words | null {
  const d = i.data;
  switch (i.kind) {
    case 'needsOk':
      return needsOkWords(i, t, lang);
    case 'visionsReady':
    case 'visionsReferral':
      return visionsItemWords(i, t, lang);
    case 'newVolunteer': {
      if (!has(d, 'name', 'dayKey')) return null;
      const opts = { name: s(d.name), id: s(d.volunteerId), day: day(d.dayKey, lang), from: s(d.heardFrom) };
      return { text: t('compItem.newVolunteer', opts), sub: d.heardFrom ? t('compItem.newVolunteerSubFrom', opts) : t('compItem.newVolunteerSub', opts) };
    }
    case 'outingTomorrow': {
      if (!has(d, 'place')) return null;
      return {
        text: t('compItem.outingTomorrow', { volunteers: t('compItem.volunteersN', { count: Number(d.volunteers ?? 0) }), tenants: t('compItem.tenantsN', { count: Number(d.tenants ?? 0) }) }),
        sub: t('compItem.outingSub', { place: s(d.place), time: s(d.time), sent: d.sent ? t('compItem.remindersSent') : t('compItem.remindersNotSent') }),
      };
    }
    case 'volunteerUnconfirmed': {
      // two items share the kind: tomorrow's reminders not sent yet (a count), and a visit today nobody confirmed
      if (i.action === 'remind') {
        if (!i.lines) return null;
        return { text: t('compItem.remind', { count: Number(d.count ?? i.lines.length) }), sub: lineList(i.lines, (l) => `${l.name} · ${time(l.minutes)}`) };
      }
      if (!has(d, 'unit', 'first', 'category', 'name')) return null;
      return { text: `${s(d.unit)} · ${s(d.first)} · ${kindOf(d, t, lang)} · ${time(d.minutes)}`, sub: t('compItem.unconfirmedSub', { name: s(d.name) }) };
    }
    case 'claimed':
      if (!has(d, 'name', 'dayKey', 'category')) return null;
      return { text: t('compItem.claimed', { name: s(d.name), day: day(d.dayKey, lang), activity: kindOf(d, t, lang) }), sub: t('compItem.claimedSub', { unit: s(d.unit), first: s(d.first), time: time(d.minutes) }) };
    case 'answers': {
      if (d.replacement) {
        if (!has(d, 'unit', 'first', 'dayKey', 'name')) return null;
        const opts = { unit: s(d.unit), first: s(d.first), day: day(d.dayKey, lang), name: s(d.name) };
        return { text: t('compItem.replacement', opts), sub: d.via === 'office' ? t('compItem.replacementOffice', opts) : t('compItem.replacementLink', opts) };
      }
      if (!has(d, 'yes', 'first', 'category') || !i.lines) return null;
      const opts = { count: Number(d.yes), first: s(d.first), activity: kindOf(d, t, lang) };
      return {
        text: d.regular ? t('compItem.answersRegular', opts) : t('compItem.answers', opts),
        sub: lineList(i.lines, (l) => (l.answer === 'yes' ? t('compItem.answerYes', { name: l.name }) : t('compItem.answerPropose', { name: l.name, day: day(l.dayKey, lang), time: time(l.minutes) }))),
      };
    }
    case 'noVolunteer': {
      if (!has(d, 'unit', 'dayKey') || typeof d.minutes !== 'number') return null;
      const n = Number(d.proposals ?? 0);
      return {
        text: t('compItem.noVolunteer', { unit: s(d.unit), day: day(d.dayKey, lang), time: time(d.minutes) }),
        sub: n ? t('compItem.noVolunteerSubProposals', { count: n }) : t('compItem.noVolunteerSub', { first: s(d.first), activity: kindOf(d, t, lang) }),
      };
    }
    case 'readingCloseout':
      if (!has(d, 'unit', 'dayKey', 'name')) return null;
      return { text: t('compItem.readingCloseout', { unit: s(d.unit), first: s(d.first), day: day(d.dayKey, lang) }), sub: t('compItem.readingCloseoutSub', { name: s(d.name), time: time(d.minutes) }) };
    case 'activityCloseout':
      if (!has(d, 'name', 'dayKey')) return null;
      return { text: t('compItem.activityCloseout', { activity: s(d.activity), day: day(d.dayKey, lang), name: s(d.name) }), sub: t('compItem.activityCloseoutSub', { name: s(d.name), time: time(d.minutes) }) };
    case 'readingNote':
      if (!has(d, 'name', 'dayKey')) return null;
      // the note is kept exactly as written
      return { text: t('compItem.readingNote', { name: s(d.name), day: day(d.dayKey, lang) }), sub: s(d.note) };
    case 'completionsPending':
      if (!i.lines) return null;
      return { text: t('compItem.completions', { count: Number(d.count ?? i.lines.length) }), sub: lineList(i.lines, (l) => t('compItem.completionLine', { name: l.name, activity: kindOf(l, t, lang), day: day(l.dayKey, lang) })) };
    case 'incidentReview': {
      if (!has(d, 'dayKey', 'name')) return null;
      const okay = d.okay === 'yes' ? t('vol.yes') : d.okay === 'no' ? t('vol.no') : s(d.okay);
      return { text: t('compItem.incident'), sub: t('compItem.incidentSub', { day: day(d.dayKey, lang), activity: s(d.activity), who: d.unit ? `${s(d.name)} – ${s(d.unit)}` : s(d.name), okay }) };
    }
    case 'followUp':
      if (!has(d, 'dayKey')) return null;
      // what the reporter wrote is kept as written; only the frame around it is translated
      return { text: t('compItem.followUp', { day: day(d.dayKey, lang) }), sub: t('compItem.followUpSub', { what: d.what ? s(d.what) : t('compItem.followUpNeeded'), owner: s(d.owner) }) };
    case 'workshopProposal':
      if (!has(d, 'title', 'dayKey')) return null;
      return { text: t('compItem.workshop', { title: s(d.title), day: day(d.dayKey, lang), time: time(d.minutes) }), sub: t('compItem.workshopSub', { by: s(d.by) }) };
    case 'activityHelp':
      if (!has(d, 'helpers', 'name', 'dayKey')) return null;
      return { text: t('compItem.activityHelp', { names: s(d.helpers), name: s(d.name), day: day(d.dayKey, lang) }), sub: t('compItem.activityHelpSub', { confirmed: Number(d.confirmed ?? 0), cap: Number(d.cap ?? 0) }) };
    default:
      return null;
  }
}

/** The item's sentence for the screen: the reader's language, else the API's English. */
export function itemWords(i: CompanionItem, t: TFunction, lang: string): Words {
  return companionItemWords(i, t, lang) ?? { text: i.text, sub: i.sub };
}

/**
 * The exact place an item is acted on, for a page that does not carry the item's own sheet (Today, the program's
 * page): the volunteer's card, the incident report, the VISIONS report, Events — else the Volunteers page, whose
 * Companion tab has the item's press. Every item gets one.
 */
export function companionItemHref(i: CompanionItem): string {
  const d = i.data;
  // 25 Oct 2026 (D-1025-PROGRAM): the program's own page carries the item's press; a volunteer's card stays on Volunteers
  const base = d.program === 'READING' ? '/reading' : '/companion';
  const q = (k: string, v: unknown, to = base) => `${to}?${k}=${encodeURIComponent(s(v))}`;
  if ((i.action === 'review' || i.kind === 'followUp') && d.incidentId) return q('incident', d.incidentId);
  if (i.action === 'volunteer' && d.volunteerId) return q('v', d.volunteerId, '/volunteers');
  if (i.action === 'call' && d.volunteerId) return q('v', d.volunteerId, '/volunteers');
  if (i.action === 'visions' && d.month) return q('visions', d.month, '/companion');
  if (i.action === 'events') return '/events#h-activities';
  if (d.requestId) return q('request', d.requestId);
  return base;
}
